import { useState } from 'react';
import { useHtmlEditorSplit } from './useHtmlEditorSplit';
import { DEFAULT_HTML_TEMPLATE_EXAMPLE, DEFAULT_PLAIN_TEMPLATE_EXAMPLE } from '@marketing-hub/shared';
import { Button } from '@/components/ui/Button';
import { Field, TextArea } from '@/components/ui/Field';
import { EmailTemplatePreviewPane } from './EmailTemplatePreviewPane';
import styles from './EmailTemplateEditor.module.css';

type HtmlEditorView = 'split' | 'code' | 'preview';

export type EnhanceHtmlOptions = {
  personalise: boolean;
  apply_brand_styling: boolean;
  skip_dna_check?: boolean;
};

type Props = {
  projectId: string | undefined;
  subject: string;
  bodyText: string;
  bodyHtml: string;
  leadId?: string;
  onBodyTextChange: (v: string) => void;
  onBodyHtmlChange: (v: string) => void;
  onSubjectChange?: (v: string) => void;
  enhanceHtml?: {
    pending: boolean;
    onRun: (opts: EnhanceHtmlOptions) => void;
    dnaReadiness: { ok: boolean; missing: string[] };
  };
};

export function EmailTemplateHtmlEditor({
  projectId,
  subject,
  bodyText,
  bodyHtml,
  leadId,
  onBodyTextChange,
  onBodyHtmlChange,
  enhanceHtml,
}: Props) {
  const [view, setView] = useState<HtmlEditorView>('split');
  const [aiPersonalise, setAiPersonalise] = useState(true);
  const [aiBrandStyle, setAiBrandStyle] = useState(true);

  const showCode = view === 'split' || view === 'code';
  const showPreview = view === 'split' || view === 'preview';
  const canPreview = Boolean(projectId && subject.trim() && bodyHtml.trim());
  const canEnhance = Boolean(projectId && bodyHtml.trim().length >= 20 && enhanceHtml);
  const split = useHtmlEditorSplit(view === 'split' && showCode && showPreview);

  function runEnhance() {
    if (!enhanceHtml || !canEnhance) return;
    let skip_dna_check = false;
    const needsDna =
      (aiPersonalise && !enhanceHtml.dnaReadiness.ok) ||
      (aiBrandStyle && !enhanceHtml.dnaReadiness.ok);
    if (needsDna) {
      const go = window.confirm(
        `Bedrijfs-DNA is nog niet compleet (${enhanceHtml.dnaReadiness.missing.join(', ')}). Toch doorgaan? Styling/personalisatie kan beperkt zijn.`,
      );
      if (!go) return;
      skip_dna_check = true;
    }
    enhanceHtml.onRun({
      personalise: aiPersonalise,
      apply_brand_styling: aiBrandStyle,
      skip_dna_check,
    });
  }

  return (
    <div className={styles.htmlEditorShell}>
      <div className={styles.htmlEditorViewBar}>
        <div className={styles.htmlEditorSegmented} role="tablist" aria-label="HTML-editor weergave">
          {(
            [
              ['split', 'Code + voorbeeld'],
              ['code', 'Alleen code'],
              ['preview', 'Alleen voorbeeld'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={view === id}
              className={[
                styles.htmlEditorSegmentedBtn,
                view === id ? styles.htmlEditorSegmentedBtnActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <span className={styles.htmlEditorViewHint}>
          Sleep tussen code en voorbeeld voor breedte · onderkant HTML-veld voor hoogte
          {split.resizable ? ' · dubbelklik op scheidingslijn = standaard' : ''}
        </span>
      </div>

      {enhanceHtml ? (
        <div className={styles.htmlAiPanel}>
          <div className={styles.htmlAiPanelHead}>
            <p className={styles.htmlAiPanelTitle}>AI HTML-verfijning</p>
            <p className={styles.htmlAiPanelHint}>
              Laat AI merge-variabelen op de juiste plek zetten en pas kleuren/fonts uit Bedrijfs-DNA toe — layout
              blijft grotendeels intact.
            </p>
          </div>
          <div className={styles.htmlAiOptions}>
            <label className={styles.htmlAiCheck}>
              <input
                type="checkbox"
                checked={aiPersonalise}
                onChange={(e) => setAiPersonalise(e.target.checked)}
              />
              Variabelen (<code>{'{{voornaam}}'}</code>, <code>{'{{company}}'}</code>, …)
            </label>
            <label className={styles.htmlAiCheck}>
              <input
                type="checkbox"
                checked={aiBrandStyle}
                onChange={(e) => setAiBrandStyle(e.target.checked)}
              />
              Merk-DNA styling (kleuren &amp; fonts)
            </label>
            <Button
              type="button"
              variant="secondary"
              disabled={!canEnhance || enhanceHtml.pending || (!aiPersonalise && !aiBrandStyle)}
              onClick={runEnhance}
            >
              {enhanceHtml.pending ? 'AI werkt…' : 'AI HTML verfijnen'}
            </Button>
          </div>
        </div>
      ) : null}

      <div
        ref={split.layoutRef}
        className={[
          styles.htmlEditorLayout,
          split.resizable ? styles.htmlEditorLayoutSplit : '',
          view === 'code' ? styles.htmlEditorLayoutCodeOnly : '',
          view === 'preview' ? styles.htmlEditorLayoutPreviewOnly : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={split.gridStyle}
      >
        {showCode ? (
          <div className={styles.htmlEditorColumn}>
            <div className={styles.htmlEditorToolbar}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  onBodyHtmlChange(DEFAULT_HTML_TEMPLATE_EXAMPLE);
                  if (!bodyText.trim()) onBodyTextChange(DEFAULT_PLAIN_TEMPLATE_EXAMPLE);
                }}
              >
                Laad HTML-voorbeeld
              </Button>
              <span className={styles.muted}>
                Vervang <code>[jouw …]</code> door je eigen tekst; variabelen blijven staan.
              </span>
            </div>
            <Field label="Body (plain text — fallback)">
              <div className={styles.htmlPlainArea}>
                <TextArea
                  value={bodyText}
                  onChange={(e) => onBodyTextChange(e.target.value)}
                  rows={4}
                />
              </div>
            </Field>
            <Field label="Body HTML">
              <div className={styles.htmlCodeArea}>
                <TextArea
                  value={bodyHtml}
                  onChange={(e) => onBodyHtmlChange(e.target.value)}
                  rows={18}
                  spellCheck={false}
                />
              </div>
            </Field>
          </div>
        ) : null}

        {split.resizable ? (
          <div
            className={styles.htmlEditorColResizer}
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={Math.round(split.previewPct)}
            aria-valuemin={22}
            aria-valuemax={78}
            aria-label="Breedte code versus live voorbeeld"
            onPointerDown={split.startDrag}
            onDoubleClick={split.resetSplit}
            title="Sleep voor breedte · dubbelklik = standaard verdeling"
          />
        ) : null}

        {showPreview ? (
          <div className={styles.htmlPreviewColumn}>
            <p className={styles.varsPanelTitle}>Live voorbeeld (voorbeeld-lead)</p>
            {canPreview ? (
              <EmailTemplatePreviewPane
                projectId={projectId!}
                subject={subject}
                blocks={[]}
                bodyText={bodyText}
                bodyHtml={bodyHtml}
                renderMode="html"
                leadId={leadId}
                presentation="inbox"
                viewport="desktop"
              />
            ) : (
              <p className={styles.muted}>Vul onderwerp en HTML in voor preview.</p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
